import {
  Badge,
  Box,
  MenuItem,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import FeeOffsettableExpenseCreateModal from "features/fees/offsets/FeeOffsettableExpenseCreateModal";
import {
  feeOffsetApplicationsToRows,
  feeOffsetRowsToRequestData,
  useFeeOffsetGetErrorForCellFn,
} from "features/fees/post/apply-offsets/FeePostApplyOffsetsData";
import { FeePostApplyOffsetsSummaryTable } from "features/fees/post/apply-offsets/FeePostApplyOffsetsSummaryTable";
import { FeePostApplyOffsetsTable } from "features/fees/post/apply-offsets/FeePostApplyOffsetsTable";
import {
  getFeeChargeEventStatsRetrieveQueryKey,
  useFeeChargeEventRetrieveSuspense,
  useFeeOffsetApplicationsListSuspense,
  useFeeOffsetApplicationsUpsert,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import { FundFamilyUseAllocationTableData } from "gen/model/fundFamilyUseAllocationTableData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { cloneDeep } from "lodash-es";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { useFundFamilyUseAllocationTableFormRowController } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableRowController";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageInstructions } from "shared/components/layout/PageInstructions";
import { PageMenu } from "shared/components/layout/PageMenu";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { formatIsoDateToDisplay } from "shared/utils/DateUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export interface FeePostApplyOffsetsPageProps {
  adjustmentFundFamilyEventId?: string;
  onClickDelete?: () => void;
}

export const FeePostApplyOffsetsPage: React.FC<
  FeePostApplyOffsetsPageProps
> = ({ adjustmentFundFamilyEventId, onClickDelete }) => {
  const { t } = useTranslation(["fees", "common"]);
  const { navigateNext } = usePageWizard();
  const queryClient = useQueryClient();

  const { fundFamilyId, feeChargeEventId } = useRequiredStringParams();

  const { data: feeChargeEvent, refetch: refetchEvent } =
    useFeeChargeEventRetrieveSuspense(fundFamilyId, feeChargeEventId);

  const { data: feeApplications, refetch: refetchApplications } =
    useFeeOffsetApplicationsListSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        adjustment_fund_family_event_id: adjustmentFundFamilyEventId,
      },
      {
        query: {
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          select: cloneDeep,
        },
      },
    );
  const { data: fundList } = useFundListSuspense({
    fund_family_ids: [fundFamilyId],
    require_commitment: true,
  });

  const { data: adjustmentFundFamilyEvent } = useFundFamilyEventRetrieve(
    fundFamilyId,
    adjustmentFundFamilyEventId!,
    // only run when adjustmentFundFamilyEventId is defined
    {
      query: {
        enabled: !!adjustmentFundFamilyEventId,
      },
    },
  );

  const {
    mutateAsync: upsertFeeOffsetApplications,
    error: upsertError,
    variables: upsertVariables,
    reset: upsertReset,
  } = useFeeOffsetApplicationsUpsert({
    mutation: {
      onSuccess: async () =>
        await Promise.all([
          refetchEvent(),
          refetchApplications(),
          invalidateQueryKeys(
            queryClient,
            getFeeChargeEventStatsRetrieveQueryKey(
              fundFamilyId,
              feeChargeEventId,
            ),
          ),
        ]),
    },
  });

  const {
    isOpen: isCreateDialogOpen,
    onOpen: openCreateDialog,
    onClose: closeCreateDialog,
  } = useDisclosure();

  const onSubmit = useCallback(
    async ({ rows }: FundFamilyUseAllocationTableData) => {
      const draftFeeOffsetApplications = feeOffsetRowsToRequestData(
        rows,
        false,
        false,
      );
      await upsertFeeOffsetApplications({
        fundFamilyId,
        feeChargeEventId,
        data: draftFeeOffsetApplications,
        params: {
          adjustment_fund_family_event_id: adjustmentFundFamilyEventId,
        },
      });
    },
    [
      feeChargeEventId,
      fundFamilyId,
      adjustmentFundFamilyEventId,
      upsertFeeOffsetApplications,
    ],
  );

  const getErrorForCell = useFeeOffsetGetErrorForCellFn(
    upsertError,
    upsertVariables,
  );

  const form = useForm<FundFamilyUseAllocationTableData>({
    defaultValues: {
      rows: feeOffsetApplicationsToRows(
        feeApplications.fee_offset_applications,
        fundList,
      ),
    },
  });

  const { rows, onRowChange, addRow } =
    useFundFamilyUseAllocationTableFormRowController(form, "rows");

  const draftAndPostedFeeOffsetApplications = useMemo(
    () => feeOffsetRowsToRequestData(rows, true, true).fee_offset_applications,
    [rows],
  );

  const isLateOffsetsWorkflow = !!adjustmentFundFamilyEventId;

  useEffect(() => {
    const newRows = feeOffsetApplicationsToRows(
      feeApplications.fee_offset_applications,
      fundList,
    );
    const existingIds = new Set(rows.map((row) => row.id));
    newRows.forEach((newRow) => {
      if (!existingIds.has(newRow.id)) {
        addRow(newRow);
      }
    });
  }, [addRow, feeApplications.fee_offset_applications, fundList, rows]);

  return (
    <PageContent>
      <Form
        form={form}
        onSubmit={onSubmit}
        onSuccess={navigateNext}
        onBeforeSubmit={upsertReset}
        onError={() => true}
      >
        <PageHeader
          size="sm"
          workflowName={
            isLateOffsetsWorkflow
              ? t("fees:lateOffsets.workflowName")
              : t("fees:postFees.workflowName")
          }
          title={t("fees:applyOffsets.title")}
          badge={{
            type: "default",
            text: feeChargeEvent.fee_charge_daterange_rich_name,
          }}
          actions={
            <>
              {isLateOffsetsWorkflow && (
                <Tooltip label={t("fees:lateOffsets.dateModal.description")}>
                  <Box
                    backgroundColor="white"
                    padding="12px"
                    rounded="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text color="gray.500" textStyle="smallBold">
                      {t("fees:lateOffsets.offsetsEffectiveDate")}
                    </Text>
                    {/* The displayed date is the effective date of the transactions. However, to compute the allocation percentages, we will use the effective date on the adjustment event which will result in using the recent allocation rules.  */}
                    <Badge mt="4px">
                      {formatIsoDateToDisplay(feeChargeEvent.transaction_date)}
                    </Badge>
                  </Box>
                </Tooltip>
              )}
              {onClickDelete && (
                <Restricted permission={PermissionTypeEnum.write}>
                  <PageMenu>
                    <MenuItem className="destructive" onClick={onClickDelete}>
                      {t("common:delete")}
                    </MenuItem>
                  </PageMenu>
                </Restricted>
              )}
            </>
          }
        />
        <PageInstructions>
          {t("fees:applyOffsets.instructions")}
        </PageInstructions>
        <PageBody>
          <FeePostApplyOffsetsSummaryTable
            feeOffsetApplications={draftAndPostedFeeOffsetApplications}
            fundFamilyId={fundFamilyId}
            feeChargeEventId={feeChargeEventId}
          />
          <FormErrorAlert
            error={upsertError}
            mb="16px"
            excludeNonFieldErrors={["fee_offset_applications"]}
          />
          <FeePostApplyOffsetsTable
            fundFamilyId={fundFamilyId}
            feeChargeEvent={feeChargeEvent}
            rows={rows}
            onRowChange={onRowChange}
            onClickAddOffset={openCreateDialog}
            feeChargeEventId={feeChargeEventId}
            adjustmentEvent={adjustmentFundFamilyEvent}
            getErrorForCell={getErrorForCell}
          />
          <PageWizardFormFooter />
        </PageBody>
      </Form>
      {isCreateDialogOpen && (
        <FeeOffsettableExpenseCreateModal
          fundFamilyId={fundFamilyId}
          onCloseComplete={closeCreateDialog}
          onSuccess={refetchApplications}
        />
      )}
    </PageContent>
  );
};
